import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Typography, 
  Grid, 
  Container, 
  Box,
  CircularProgress,
  Alert,
  Collapse
} from '@mui/material';
import { Google as GoogleIcon } from '../../icons/google';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from './Input';
import jwt_decode from 'jwt-decode';

const Auth = ({ isModal = false, onLoginSuccess }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ 
    firstName: '', 
    lastName: '', 
    email: '', 
    password: '', 
    confirmPassword: '' 
  });
  const clientID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const navigate = useNavigate();
  const { signIn } = useAuth();

  // Check if running in an in-app browser
  const isInAppBrowser = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /LinkedInApp|FBAN|FBAV|Instagram|Line|Snapchat|Twitter|WhatsApp/i.test(userAgent);
  };

  useEffect(() => {
    if (window.google && clientID) {
      google.accounts.id.initialize({
        client_id: clientID,
        callback: handleGoogleSignIn,
        auto_select: false,
        cancel_on_tap_outside: true,
        // Add configuration for better in-app browser support
        prompt_parent_id: 'root',
        use_fedcm_for_prompt: true,
        // Disable auto prompt in in-app browsers
        auto_prompt: !isInAppBrowser()
      });

      // Render the Google Sign-In button if we're in an in-app browser
      if (isInAppBrowser()) {
        const buttonElement = document.getElementById('google-signin-button');
        if (buttonElement) {
          google.accounts.id.renderButton(buttonElement, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: '100%'
          });
        }
      }
    }
  }, [clientID]);

  const handleGoogleSignIn = async (response) => {
    setLoading(true);
    setError('');
    
    try {
        const decoded = jwt_decode(response.credential);

        // Split the name into first and last name
        const nameParts = decoded.name.split(' ');
        const firstName = nameParts[0];
        const lastName = nameParts.slice(1).join(' ');

        const userData = {
            googleId: decoded.sub,
            email: decoded.email,
            name: decoded.name,
            firstName: firstName,
            lastName: lastName || '',
            imageUrl: decoded.picture,
            password: null,
            isGoogleUser: true  // Add this flag to identify Google users
        };

        const user = await signIn(userData);
        
        if (user) {
            if (isModal && onLoginSuccess) {
                onLoginSuccess(user);
            } else {
                // Check if the user needs to complete their profile
                if (user.isNewUser) {
                    navigate('/account', { 
                        state: { 
                            isNewUser: true,
                            initialData: userData
                        }
                    });
                } else {
                    navigate('/');
                }
            }
        }
    } catch (error) {
        console.error('Google Sign In Error:', error);
        setError(error.message || 'Failed to sign in with Google. Please try again.');
    } finally {
        setLoading(false);
    }
  };

  const handleGoogleClick = () => {
    setError('');
    if (window.google && clientID) {
      try {
        // For in-app browsers, try to use a more compatible approach
        if (isInAppBrowser()) {
          // Try to trigger the Google Sign-In flow
          google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed()) {
              setError('Google Sign-In popup was blocked. Please try opening this app in your regular browser or sign in with email.');
            } else if (notification.isSkippedMoment()) {
              setError('Google Sign-In was skipped. Please try again or sign in with email.');
            } else if (notification.isDismissedMoment()) {
              setError('Google Sign-In was dismissed. Please try again or sign in with email.');
            }
          });
        } else {
          // Standard prompt for regular browsers
          google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
              setError('Google Sign-In is not available. Please try again later.');
            }
          });
        }
      } catch (error) {
        console.error('Google Sign-In initialization error:', error);
        setError('Google Sign-In is not available in this browser. Please try using a different browser or sign in with email.');
      }
    } else {
      setError('Google Sign In is not available. Please try again later.');
    }
  };

  const handleShowPassword = () => setShowPassword((prevShowPassword) => !prevShowPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
        if (!isSignUp) {
            const user = await signIn({
                email: formData.email,
                password: formData.password
            });
            
            if (isModal && onLoginSuccess) {
                onLoginSuccess(user);
            } else {
                navigate('/');
            }
        } else {            
            navigate('/account', { 
                state: { 
                    isNewUser: true,
                    initialData: {
                        email: formData.email,
                        password: formData.password,
                        name: `${formData.firstName} ${formData.lastName}`.trim()
                    }
                },
                replace: true
            });
        }
    } catch (error) {
        setError(error.message || 'Authentication failed. Please try again.');
    } finally {
        setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value});
  };

  const switchMode = () => {
    if (!isSignUp) {
        // When user clicks "Don't have an account? Sign Up"
        navigate('/create-account', { 
            state: { 
                isNewUser: true,
                initialData: {
                    email: formData.email,
                    password: formData.password,
                    name: ''
                }
            },
            replace: true
        });
    } else {
        // When user clicks "Already have an account? Sign In"
        setIsSignUp(false);
        setShowPassword(false);
    }
  };

  return (
    <Box component="main" sx={{alignItems: 'center', display: 'flex', flexGrow: 1, minHeight: '100%'}}>
      <Container maxWidth="sm">
        <form onSubmit={handleSubmit}>
          <Box sx={{ my: 3 }}>
            <Typography color="textPrimary" variant="h4" >
              { isSignUp ? "Create a new account" : "Sign in" }
            </Typography>
            <Typography color="textSecondary" gutterBottom variant="body2" >
              { isSignUp ? "Use your email to create a new account" : "Sign in on the internal platform" }
            </Typography>
          </Box>                       
          <Collapse in={!!error}>
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          </Collapse>
          {!isSignUp && (
            <>
              <Box sx={{ py: 2 }}>
                {/* Container for Google Sign-In button */}
                <div id="google-signin-button" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}></div>
                {/* Fallback button for when Google Sign-In is not available */}
                <Button 
                  color="error" 
                  fullWidth 
                  onClick={handleGoogleClick}
                  size="large"
                  startIcon={!loading && <GoogleIcon />}
                  variant="contained"
                  disabled={loading}
                  sx={{ mt: 1 }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Login with Google'
                  )}
                </Button>
                {isInAppBrowser() && (
                  <Typography 
                    variant="caption" 
                    color="textSecondary" 
                    align="center" 
                    sx={{ mt: 1, display: 'block' }}
                  >
                    If Google Sign-In doesn't work, try opening this app in your regular browser
                  </Typography>
                )}
              </Box>
              <Box sx={{ pb: 1, pt: 3 }}>
                <Typography align="center" color="textSecondary" variant="body1">
                  or login with email address
                </Typography>
              </Box>
            </>
          )}
          <Grid container spacing={2}>        
            {
              isSignUp && (
                <>
                  <Input name="firstName" label="First Name" margin="normal" autoFocus handleChange={handleChange} />
                  <Input name="lastName" label="Last Name" margin="normal" handleChange={handleChange} />                                
                </>
              )
            }
            <Input name="email" label="Email Address" handleChange={handleChange} type="email" />
            <Input name="password" label="Password" handleChange={handleChange} type={showPassword ? "text" : "password"} handleShowPassword={handleShowPassword} />
            { isSignUp && <Input name="confirmPassword" label="Confirm Password" handleChange={handleChange} type="password" />}
          </Grid>
          <Box sx={{ py: 2 }}>
            <Button 
              color="primary" 
              fullWidth 
              size="large" 
              type="submit" 
              variant="contained"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                isSignUp ? "Sign Up Now" : "Sign In Now"
              )}
            </Button>
          </Box>                        
          <Typography color="textSecondary" variant="body2" >
            <Button 
              onClick={switchMode}
              disabled={loading}
            >
              {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
            </Button>
          </Typography>                                                    
        </form>
      </Container>
    </Box>
  );
};

export default Auth;